import React, { useEffect, useRef, useState } from 'react';
import './LogoLoop.css';

const LogoLoop = ({
    logos = [],
    speed = 100,
    direction = 'left',
    logoHeight = 60,
    gap = 60,
    hoverSpeed = 0,
    scaleOnHover = false,
    fadeOut = false,
    fadeOutColor = '#ffffff',
    ariaLabel = 'Logo carousel',
    useCustomRender = false,
}) => {
    const trackRef = useRef(null);
    const [isPaused, setIsPaused] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;

        let animationId = null;
        let position = 0;
        let timeoutId = null;
        let isCleanedUp = false;
        
        // Calculate width of one complete set of logos
        // Increase duplicates for longer lists to ensure continuous scroll
        const baseDuplicates = isMobile ? 8 : 2;
        const duplicateCount = isMobile && logos.length > 12 ? 10 : baseDuplicates;
        
        // Wait for layout to settle and images to populate
        timeoutId = setTimeout(() => {
            if (isCleanedUp) return;
            
            const singleSetWidth = track.scrollWidth / duplicateCount;
            
            if (singleSetWidth === 0 || !singleSetWidth) return;

            const animate = () => {
                if (isCleanedUp) return;
                
                if (!isPaused) {
                    const moveSpeed = speed / 60;
                    
                    if (direction === 'left') {
                        position += moveSpeed;
                        // Reset when we've scrolled one full set
                        if (position >= singleSetWidth) {
                            position = position - singleSetWidth;
                        }
                    } else {
                        // direction === 'right'
                        position -= moveSpeed;
                        // Reset when we've scrolled one full set backwards
                        if (position <= -singleSetWidth) {
                            position = position + singleSetWidth;
                        }
                    }

                    track.style.transform = `translate3d(${-position}px, 0, 0)`;
                }
                animationId = requestAnimationFrame(animate);
            };

            animationId = requestAnimationFrame(animate);
        }, isMobile ? 50 : 100);

        return () => {
            isCleanedUp = true;
            if (timeoutId) clearTimeout(timeoutId);
            if (animationId) cancelAnimationFrame(animationId);
        };
    }, [speed, direction, isPaused, isMobile, logos.length]);

    const handleMouseEnter = () => {
        if (hoverSpeed === 0) {
            setIsPaused(true);
        }
    };

    const handleMouseLeave = () => {
        setIsPaused(false);
    };

    const renderLogo = (logo, index, setIndex = 0) => {
        const content = logo.src ? (
            <img src={logo.src} alt={logo.alt || `Logo ${index + 1}`} />
        ) : (
            <div className="logoloop__node">{logo.node}</div>
        );

        return logo.href ? (
            <a
                href={logo.href}
                className="logoloop__link"
                target="_blank"
                rel="noopener noreferrer"
                title={logo.title || logo.alt}
            >
                {content}
            </a>
        ) : (
            content
        );
    };

    // More duplicates for mobile to ensure smooth looping
    // Increase duplicates for longer lists to ensure continuous scroll
    const baseDuplicates = isMobile ? 8 : 2;
    const duplicateCount = isMobile && logos.length > 12 ? 10 : baseDuplicates;

    return (
        <div
            className={`logoloop ${scaleOnHover ? 'logoloop--scale-hover' : ''} ${fadeOut ? 'logoloop--fade' : ''
                }`}
            style={{
                '--logoloop-gap': `${gap}px`,
                '--logoloop-logoHeight': `${logoHeight}px`,
                '--logoloop-fadeColor': fadeOutColor,
            }}
            aria-label={ariaLabel}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div ref={trackRef} className="logoloop__track">
                {Array.from({ length: duplicateCount }).map((_, setIndex) => (
                    <div key={setIndex} className="logoloop__list">
                        {logos.map((logo, index) => (
                            <div key={`${setIndex}-${index}`} className="logoloop__item">
                                {renderLogo(logo, index, setIndex)}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default LogoLoop;
